import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronSquareRight, faChevronSquareDown } from '@fortawesome/pro-solid-svg-icons';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import React from 'react';
import {
	CerebroIcon,
	CerebroStatusIndicatorTableCell,
	CerebroText,
	StatusIndicatorColor,
} from '../../shared/components';
import { DirectConnectMilestoneReport, DataRoom, DataRoomUser } from '../../shared/models';
import { TableCellMilestoneIcon } from './TableCellMilestoneIcon';
import { useAuth } from '../../shared/context';
import { DateUtils } from '../../shared/utils';
import { GetDataRoomUserFromDirectConnectMilestoneReport } from '../../shared/utils/entity';
import { DirectConnectParticipantActionButton } from './DirectConnectParticipantActionButton';

interface DirectConnectMilestoneRowProps {
	milestone: DirectConnectMilestoneReport;
	dataRoom: DataRoom;
	onDetails: (milestone: DirectConnectMilestoneReport | null) => void;
	optOutUser: (user: DataRoomUser) => void;
	sendEmail: (user: DataRoomUser) => void;
	contactDetails: (user: DataRoomUser) => void;
}

export const DirectConnectMilestoneRow: React.FC<DirectConnectMilestoneRowProps> = ({
	milestone,
	dataRoom,
	onDetails,
	optOutUser,
	sendEmail,
	contactDetails,
}) => {
	const { currentUser } = useAuth();
	const user = GetDataRoomUserFromDirectConnectMilestoneReport(milestone) ?? ({} as DataRoomUser);
	const [toggleDetailRow, setToggleDetailRow] = React.useState<boolean>(false);

	const getStatusColor = (milestone: DirectConnectMilestoneReport): StatusIndicatorColor => {
		if (milestone.activeUser) {
			return 'Green';
		}

		return milestone.optedOut ||
			milestone.roomDeclineReasonList?.length > 0 ||
			milestone.otherDeclineReason ||
			milestone.otherRoomDeclineReason
			? 'Red'
			: 'Yellow';
	};

	const getStatusTooltip = (milestone: DirectConnectMilestoneReport): string => {
		if (milestone.activeUser) {
			return 'Active';
		}

		return milestone.optedOut ||
			milestone.roomDeclineReasonList?.length > 0 ||
			milestone.otherDeclineReason ||
			milestone.otherRoomDeclineReason
			? 'Opted-Out'
			: 'Inactive';
	};

	return (
		<>
			<TableRow>
				<CerebroStatusIndicatorTableCell
					colorType={getStatusColor(milestone)}
					tooltip={getStatusTooltip(milestone)}
				/>
				<TableCell>
					<CerebroIcon
						sx={{ cursor: 'pointer' }}
						title={toggleDetailRow ? 'Hide lender detail row' : 'Show lender detail row'}
						onClick={() => setToggleDetailRow(!toggleDetailRow)}
					>
						{toggleDetailRow ? (
							<FontAwesomeIcon icon={faChevronSquareDown} size="lg" />
						) : (
							<FontAwesomeIcon icon={faChevronSquareRight} size="lg" />
						)}
					</CerebroIcon>
				</TableCell>
				<TableCell>
					<CerebroText>{milestone.bankName}</CerebroText>
				</TableCell>
				<TableCellMilestoneIcon
					milestoneType={'DocumentsDownloaded'}
					response={milestone.documentsDownloaded}
					inProgress={false}
				/>
				<TableCellMilestoneIcon
					milestoneType={'TermSheetSubmitted'}
					response={milestone.termSheetSubmitted}
					inProgress={false}
				/>
				<TableCellMilestoneIcon
					milestoneType={'Underwriting'}
					response={milestone.underwriting}
					inProgress={false}
				/>
				<TableCellMilestoneIcon milestoneType={'Funded'} response={milestone.funded} inProgress={false} />
				<TableCell>
					<DirectConnectParticipantActionButton
						isEmployee={currentUser.isEmployeeUser}
						dataRoom={dataRoom}
						user={user}
						milestone={milestone}
						optOutUser={optOutUser}
						sendEmail={sendEmail}
						progressDetails={onDetails}
						contactDetails={contactDetails}
					/>
				</TableCell>
			</TableRow>
			{toggleDetailRow && (
				<TableRow>
					<TableCell colSpan={2}></TableCell>
					<TableCell colSpan={6}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Phone</TableCell>
									<TableCell>Last Accessed</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									<TableCell>
										{milestone.firstName} {milestone.lastName}
									</TableCell>
									<TableCell>{milestone.emailAddress}</TableCell>
									<TableCell>{milestone.phone}</TableCell>
									<TableCell>
										{DateUtils.FormatStringAsShortDate(milestone.lastAccessedOn?.toString())}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};
